import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import { CircularProgress } from "@mui/material";
import { Send } from "lucide-react";
import ErrorText from "../../common/ErrorText";
import apiCall from "../../../helpers/apifactory";

const shipmentOptions = [
  { value: "1-50", label: "1 - 50" },
  { value: "50-100", label: "50 - 100" },
  { value: "100-150", label: "100 - 150" },
  { value: "150+", label: "150 +" },
];

const categories = [
  { value: "Auto Parts & Accessories", label: "Auto Parts & Accessories" },
  { value: "Beauty & Cosmetics", label: "Beauty & Cosmetics" },
  { value: "Books & Stationery", label: "Books & Stationery" },
  {
    value: "Electronics & Mobile Accessories",
    label: "Electronics & Mobile Accessories",
  },
  { value: "Fashion & Apparel", label: "Fashion & Apparel" },
  { value: "Food & Beverages", label: "Food & Beverages" },
  { value: "Grocery", label: "Grocery" },
  { value: "Health & Wellness", label: "Health & Wellness" },
  { value: "Home & Kitchen", label: "Home & Kitchen" },
  { value: "Jewellery & Accessories", label: "Jewellery & Accessories" },
  { value: "Sports & Fitness", label: "Sports & Fitness" },
  { value: "Other", label: "Other" },
];

const RetailCOD = () => {
  const [cities, setCities] = useState([]);
  const [isCitiesLoading, setIsCitiesLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const formik = useFormik({
    validateOnMount: true,
    validateOnChange: true,

    initialValues: {
      name: "",
      phoneNumber: "",
      city: null,
      natureOfBusiness: "",
      monthlyShipments: "",
    },

    validationSchema: Yup.object({
      name: Yup.string().trim().required("This is Required"),
      phoneNumber: Yup.string()
        .matches(
          /^((\+92)?(0092)?(92)?(0)?)(3)([0-9]{9})$/,
          "Invalid phone number",
        )
        .required("This is Required"),
      city: Yup.object().nullable().required("This is Required"),
      natureOfBusiness: Yup.string().trim().required("This is Required"),
      monthlyShipments: Yup.string().required(
        "Please select monthly shipments",
      ),
    }),

    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        setErrorMessage("");

        // Map shipment options to Max volume
        const volumeMapping = {
          "1-50": 50,
          "50-100": 100,
          "100-150": 150,
          "150+": 150,
        };
        // Map form values to createRcodLead API payload
        const body = {
          origincity: values?.city?.value,
          categories: values?.natureOfBusiness,
          phonenumber: Number(values?.phoneNumber),
          name: values?.name,
          shipment_volume: volumeMapping[values?.monthlyShipments],
          product_code: "RCOD",
        };

        // console.log("Retail COD Payload:", body);
        const response = await apiCall.createRcodLead(body);
        const data = response?.data;
        if (data?.isSuccess && data?.responseData?.code === "200") {
          formik.resetForm();
          setShowSuccess(true);
        } else {
          setErrorMessage(
            data?.responseData?.message || "Failed to submit request.",
          );
        }
      } catch (error) {
        setErrorMessage(
          error?.response?.data?.responseData?.message ||
            "An unexpected error occurred. Please try again.",
        );
        console.error("Retail COD Error:", error);
      } finally {
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    setIsCitiesLoading(true);

    apiCall
      .getCity()
      .then((res) => {
        const cityArray =
          res?.data?.responseData?.cities?.map(
            ({ station_code, station_name }) => ({
              value: station_code,
              label: station_name,
            }),
          ) || [];

        setCities(cityArray);
      })
      .catch((error) => {
        console.log("City API Error:", error);
        setCities([]);
      })
      .finally(() => {
        setIsCitiesLoading(false);
      });
  }, []);

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");

    if (value.length <= 11) {
      formik.setFieldValue("phoneNumber", value);
    }
  };

  const handleClear = () => {
    formik.resetForm();
  };

  const handleExit = () => {
    window.location.href = "/ecom-solution/retail_cod";
  };

  return (
    <>
      <div className="bg-[#f7f7f8]">
        <div className="relative w-full h-[100px] sm:h-[110px] bg-white overflow-hidden">
          <div className="absolute top-[25px] sm:top-[35px] left-0 w-full h-[55px] sm:h-[70px] bg-[#ED1C24]" />

          <h1 className="absolute left-5 sm:left-10 lg:left-[65px] top-[48px] sm:top-[48px] z-20 text-white text-[21px] sm:text-[34px] lg:text-[42px] font-bold leading-none whitespace-nowrap">
            Retail COD Form
          </h1>

          <div
            className="absolute right-0 lg:right-[90px] top-0 w-[160px] sm:w-[220px] lg:w-[330px] h-full bg-white flex items-center justify-center"
            style={{
              clipPath: "path('M35 0 H295 Q330 52 295 105 H35 Q0 52 35 0 Z')",
            }}
          >
            <img
              src="/r-cod.png"
              alt="RCOD Logo"
              className="w-[110px] sm:w-[160px] lg:w-[220px] object-contain"
            />
          </div>
        </div>

        <main className="max-w-[1130px] mx-auto px-4 sm:px-8 py-8 sm:py-[4rem]">
          <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_0.90fr] gap-6 items-start">
            <form
              onSubmit={formik.handleSubmit}
              className="relative overflow-hidden bg-white border border-gray-100 rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.08)] p-6 sm:p-8 lg:p-9"
            >
              <div className="absolute top-0 left-0 w-full h-[6px] bg-gradient-to-r from-[#D40511] via-[#ff5252] to-[#D40511] rounded-t-3xl" />
              <div className="mb-4">
                <div className="relative">
                  <input
                    type="text"
                    id="name"
                    name="name"
                    maxLength={49}
                    placeholder=" "
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="peer block w-full h-[40px] px-2.5 text-sm text-gray-800 bg-white rounded border border-gray-300 outline-none transition-all duration-200 hover:border-gray-500 focus:border-[#D40511] focus:ring-1 focus:ring-[#D40511]"
                  />

                  <label
                    htmlFor="name"
                    className="absolute left-2 top-[-7px] bg-white px-1.5 text-[11px] text-gray-500 transition-all duration-200 peer-placeholder-shown:top-[10px] peer-placeholder-shown:text-sm peer-focus:top-[-7px] peer-focus:text-[11px] peer-focus:text-[#D40511]"
                  >
                    Name
                    <span className="text-[#D40511]"> *</span>
                  </label>
                </div>

                <ErrorText text={formik.touched.name && formik.errors.name} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      id="phoneNumber"
                      name="phoneNumber"
                      maxLength={11}
                      placeholder=" "
                      value={formik.values.phoneNumber}
                      onChange={handlePhoneChange}
                      onBlur={formik.handleBlur}
                      className="peer block w-full h-[40px] px-2.5 text-sm text-gray-800 bg-white rounded border border-gray-300 outline-none transition-all duration-200 hover:border-gray-500 focus:border-[#D40511] focus:ring-1 focus:ring-[#D40511]"
                    />

                    <label
                      htmlFor="phoneNumber"
                      className="absolute left-2 top-[-7px] bg-white px-1.5 text-[11px] text-gray-500 transition-all duration-200 peer-placeholder-shown:top-[10px] peer-placeholder-shown:text-sm peer-focus:top-[-7px] peer-focus:text-[11px] peer-focus:text-[#D40511]"
                    >
                      Phone Number
                      <span className="text-[#D40511]"> *</span>
                    </label>
                  </div>

                  <ErrorText
                    text={
                      formik.touched.phoneNumber && formik.errors.phoneNumber
                    }
                  />
                </div>

                <div>
                  <Select
                    className="select1"
                    placeholder="City *"
                    name="city"
                    id="city"
                    isLoading={isCitiesLoading}
                    value={formik.values.city}
                    options={cities}
                    onChange={(option) => {
                      formik.setFieldValue("city", option);
                    }}
                    onBlur={() => {
                      formik.setFieldTouched("city", true);
                    }}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        height: "40px",
                        minHeight: "40px",
                        borderRadius: "4px",
                        borderColor: state.isFocused ? "#D40511" : "#d1d5db",
                        boxShadow: state.isFocused
                          ? "0 0 0 1px #D40511"
                          : "none",
                        fontSize: "14px",

                        "&:hover": {
                          borderColor: state.isFocused ? "#D40511" : "#6b7280",
                        },
                      }),

                      valueContainer: (base) => ({
                        ...base,
                        height: "38px",
                        padding: "0 9px",
                      }),

                      input: (base) => ({
                        ...base,
                        margin: "0px",
                        padding: "0px",
                      }),

                      indicatorsContainer: (base) => ({
                        ...base,
                        height: "38px",
                      }),

                      placeholder: (base) => ({
                        ...base,
                        color: "#6b7280",
                        fontSize: "14px",
                      }),

                      menu: (base) => ({
                        ...base,
                        zIndex: 100,
                        fontSize: "14px",
                      }),
                    }}
                  />

                  <ErrorText text={formik.touched.city && formik.errors.city} />
                </div>
              </div>

              <div className="mb-5">
                <div className="relative">
                  <Select
                    className="select1"
                    placeholder="Nature of Business *"
                    name="natureOfBusiness"
                    id="natureOfBusiness"
                    options={categories}
                    value={
                      categories.find(
                        (option) =>
                          option.value === formik.values.natureOfBusiness,
                      ) || null
                    }
                    onChange={(option) => {
                      formik.setFieldValue(
                        "natureOfBusiness",
                        option?.value || "",
                      );
                    }}
                    onBlur={() => {
                      formik.setFieldTouched("natureOfBusiness", true);
                    }}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        height: "40px",
                        minHeight: "40px",
                        borderRadius: "4px",
                        borderColor: state.isFocused ? "#D40511" : "#d1d5db",
                        boxShadow: state.isFocused
                          ? "0 0 0 1px #D40511"
                          : "none",
                        fontSize: "14px",
                        "&:hover": {
                          borderColor: state.isFocused ? "#D40511" : "#6b7280",
                        },
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      valueContainer: (base) => ({
                        ...base,
                        height: "38px",
                        padding: "0 9px",
                      }),
                      input: (base) => ({
                        ...base,
                        margin: "0px",
                        padding: "0px",
                      }),
                      indicatorsContainer: (base) => ({
                        ...base,
                        height: "38px",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "#6b7280",
                        fontSize: "14px",
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 100,
                        fontSize: "14px",
                      }),
                    }}
                  />
                </div>

                <ErrorText
                  text={
                    formik.touched.natureOfBusiness &&
                    formik.errors.natureOfBusiness
                  }
                />
              </div>

              <div className="mb-4">
                <div className="mb-3">
                  <h3 className="text-sm font-semibold text-gray-800">
                    Monthly Number of Shipments
                    <span className="text-[#D40511]"> *</span>
                  </h3>

                  <p className="text-[11px] text-gray-400 mt-1">
                    Select your estimated monthly shipment volume.
                  </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {shipmentOptions.map((option) => {
                    const selected =
                      formik.values.monthlyShipments === option.value;

                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={async () => {
                          await formik.setFieldValue(
                            "monthlyShipments",
                            option.value,
                            true,
                          );
                          formik.setFieldTouched(
                            "monthlyShipments",
                            true,
                            false,
                          );
                        }}
                        className={`relative h-[46px] rounded-lg border text-[13px] font-semibold transition-all duration-200 ${selected ? "border-[#D40511] bg-red-50 text-[#D40511] ring-1 ring-[#D40511]" : "border-gray-200 bg-white text-gray-600 hover:border-[#D40511] hover:bg-red-50/40"}`}
                      >
                        {selected && (
                          <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-[#D40511] text-white flex items-center justify-center text-[9px]">
                            ✓
                          </div>
                        )}
                        {option.label}
                      </button>
                    );
                  })}
                </div>

                <ErrorText
                  text={
                    formik.touched.monthlyShipments &&
                    !formik.values.monthlyShipments &&
                    formik.errors.monthlyShipments
                  }
                />
              </div>

              <div className="pt-3 border-t border-gray-100 flex flex-col items-end sm:flex-row sm:items-center sm:justify-between gap-3">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="order-1 w-full sm:w-auto h-[40px] min-w-[155px] px-7 rounded bg-[#D40511] text-white text-[13px] font-bold flex items-center justify-center gap-2 hover:scale-[1.02] transition-all duration-300 hover:bg-[#b9040e] hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <CircularProgress size={16} sx={{ color: "white" }} />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      <span>Submit Request</span>
                    </>
                  )}
                </button>

                <div className="flex gap-2 order-2">
                  <button
                    type="button"
                    onClick={handleClear}
                    className="h-[38px] px-6 rounded border border-gray-300 bg-white text-xs font-semibold text-gray-600 transition-all duration-200 hover:bg-[#fff1f2] hover:border-[#D40511] hover:text-[#D40511]"
                  >
                    Clear
                  </button>

                  <button
                    type="button"
                    onClick={handleExit}
                    className="h-[38px] px-6 rounded border border-gray-300 bg-white text-xs font-semibold text-gray-600 transition-all duration-200 hover:bg-[#fff1f2] hover:border-[#D40511] hover:text-[#D40511]"
                  >
                    Exit
                  </button>
                </div>
              </div>
            </form>

            <aside className="lg:sticky lg:top-6">
              <div
                className="
                bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-[0_5px_25px_rgba(0,0,0,0.05)]"
              >
                <img
                  src="/DomesticScrollingBanner/R-cod.jpg"
                  alt="Retail COD"
                  loading="lazy"
                  className="block w-full h-auto object-cover"
                />
              </div>
            </aside>
          </div>
        </main>
      </div>

      {isLoading && (
        <div className="fixed inset-0 z-[9999] bg-black/20 backdrop-blur-[1px] flex justify-center items-center">
          <div className="bg-white rounded-2xl shadow-xl px-8 py-6 flex flex-col items-center gap-3">
            <CircularProgress size={38} sx={{ color: "#D40511" }} />
            <span className="text-sm font-medium text-gray-600">
              Submitting your request...
            </span>
          </div>
        </div>
      )}

      {showSuccess && (
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/40backdrop-blur-[2px] px-4"
          onClick={() => setShowSuccess(false)}
        >
          <div
            className="relative w-full max-w-[460px] overflow-hidden rounded-2xl bg-white shadow-[0_25px_70px_rgba(0,0,0,0.25)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-[4px] w-full bg-[#D40511]" />

            <button
              type="button"
              onClick={() => setShowSuccess(false)}
              aria-label="Close"
              className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full text-gray-400 transition-all hover:bg-gray-100 hover:text-[#D40511]"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="h-5 w-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 6l12 12M18 6L6 18"
                />
              </svg>
            </button>

            <div className="px-7 sm:px-10 py-[4rem] text-center">
              <div className="mx-auto mb-5 flex h-[65px] w-[65px] items-center justify-center rounded-full bg-green-50">
                <div className="flex h-[47px] w-[47px] items-center justify-center rounded-full bg-[#16A34A]">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2.5"
                    className="h-6 w-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 12.5l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>

              <h2 className="text-[25px] font-bold text-[#202124]">
                Thanks, you’re all set.
              </h2>

              <div className="mx-auto my-4 h-[3px] w-10 rounded-full bg-[#D40511]" />

              <p className="text-[14px] leading-6 text-gray-500">
                Your details have been submitted successfully. Thank you for
                your interest in{" "}
                <span className="font-semibold text-gray-800">
                  TCS Retail-COD!
                </span>
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Error Modal */}
      {errorMessage && (
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/40 backdrop-blur-[2px] px-4"
          onClick={() => setErrorMessage("")}
        >
          <div
            className="relative w-full max-w-[460px] overflow-hidden rounded-2xl bg-white shadow-[0_25px_70px_rgba(0,0,0,0.25)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-[4px] w-full bg-[#D40511]" />

            <button
              type="button"
              onClick={() => setErrorMessage("")}
              aria-label="Close"
              className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full text-gray-400 transition-all hover:bg-gray-100 hover:text-[#D40511]"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="h-5 w-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 6l12 12M18 6L6 18"
                />
              </svg>
            </button>

            <div className="px-7 sm:px-10 py-[3.5rem] text-center">
              <div className="mx-auto mb-5 flex h-[65px] w-[65px] items-center justify-center rounded-full bg-red-50">
                <div className="flex h-[47px] w-[47px] items-center justify-center rounded-full bg-[#D40511]">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2.5"
                    className="h-6 w-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
              </div>

              <h2 className="text-[25px] font-bold text-[#202124]">
                Submission Failed
              </h2>

              <div className="mx-auto my-4 h-[3px] w-10 rounded-full bg-[#D40511]" />

              <p className="text-[14px] leading-6 text-gray-500">
                {errorMessage}
              </p>

              <button
                type="button"
                onClick={() => setErrorMessage("")}
                className="mt-6 h-[40px] px-8 rounded bg-[#D40511] text-white text-[13px] font-bold hover:bg-[#b9040e] transition-colors shadow-sm"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RetailCOD;
